<script setup>
import { Handle, Position, useNode } from '@vue-flow/core';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { ACTION_TYPES } from '../../constants/nodeTypes.js';
import { FLOW_VARIABLE_LABELS } from 'flow_builder/constants/flowVariables.js';
import { useNodeAudience } from '../../composables/useNodeAudience.js';

const props = defineProps({
  data: { type: Object, default: () => ({}) },
  selected: { type: Boolean, default: false },
});

const { t } = useI18n();
const { id } = useNode();
const audienceCount = useNodeAudience(id);

const actionLabel = computed(() => {
  const action = ACTION_TYPES.find(a => a.key === props.data.action_name);
  return action ? t(action.label) : t('FLOW_BUILDER.NODES.ACTION.SELECT_ACTION');
});

function parseContent(text) {
  if (!text) return [];
  const tokens = [];
  const regex = /(\{\{[\w.]+\}\})/g;
  let last = 0;
  let m;
  while ((m = regex.exec(text)) !== null) {
    if (m.index > last) tokens.push({ type: 'text', value: text.slice(last, m.index) });
    const key = m[1].slice(2, -2);
    tokens.push({ type: 'var', value: FLOW_VARIABLE_LABELS[key] || key });
    last = m.index + m[0].length;
  }
  if (last < text.length) tokens.push({ type: 'text', value: text.slice(last) });
  return tokens;
}

const contentTokens = computed(() => {
  const params = props.data.action_params;
  if (!params) return [];
  if (params.message_type === 'template') {
    const tpl = params.template;
    if (!tpl) return [];
    const body = tpl.components?.find(c => c.type === 'BODY')?.text ?? tpl.body ?? '';
    return parseContent(body);
  }
  return parseContent(params.content ?? '');
});

const headerMediaUrl = computed(() => {
  const params = props.data.action_params;
  if (params?.message_type !== 'template') return null;
  return params?.template_header_media_url || null;
});
</script>

<template>
  <div
    class="flow-node w-[180px] rounded-xl overflow-hidden shadow-md border-2 transition-all"
    :class="selected ? 'border-n-blue-8' : 'border-transparent'"
  >
    <Handle type="target" :position="Position.Top" />
    <div class="flex items-center gap-2 px-3 py-2 bg-n-blue-9 text-white">
      <span class="i-lucide-zap size-4 shrink-0" />
      <span class="text-xs font-semibold truncate flex-1">{{ data.name || t('FLOW_BUILDER.NODES.ACTION.TITLE') }}</span>
      <span v-if="audienceCount !== null" class="text-[9px] bg-white/20 rounded-full px-1.5 py-0.5 font-medium tabular-nums shrink-0">
        {{ audienceCount }}
      </span>
    </div>
    <div class="px-3 py-2 bg-n-solid-1 text-xs flex flex-col gap-1">
      <span class="text-n-slate-12 truncate">{{ actionLabel }}</span>
    </div>
    <div v-if="headerMediaUrl" class="relative w-full h-20 bg-n-alpha-2">
      <img
        :src="headerMediaUrl"
        class="w-full h-full object-cover"
        alt=""
        @error="$event.target.style.display='none'"
      />
      <span
        class="absolute inset-0 flex items-center justify-center i-lucide-image text-n-slate-9 size-6 pointer-events-none"
        style="display:none"
      />
    </div>
    <div v-if="contentTokens.length" class="px-3 py-2 bg-n-solid-1 text-xs">
      <span class="line-clamp-2 text-[10px] leading-4 text-n-slate-10">
        <template v-for="(tok, i) in contentTokens" :key="i">
          <span v-if="tok.type === 'text'">{{ tok.value }}</span>
          <span v-else class="inline rounded bg-n-blue-3 text-n-blue-11 px-0.5 font-mono">{{ tok.value }}</span>
        </template>
      </span>
    </div>
    <Handle type="source" :position="Position.Bottom" />
  </div>
</template>
